import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, LinkIcon, Coins, Mail, MoreHorizontal, ArrowLeft, Loader2, Camera, X, Plus, Ban, VolumeX, Flag, Check, Flame, Eye, Share2, Users } from "lucide-react";
import { toast } from "sonner";
import { AuraRing } from "@/components/brand/AuraRing";
import { VerifiedBadge } from "@/components/brand/VerifiedBadge";
import { OgBadge } from "@/components/brand/OgBadge";
import { RightSidebar } from "@/components/app/RightSidebar";
import { FollowButton } from "@/components/app/FollowButton";
import { AuraBadge } from "@/components/app/AuraBadge";
import { AuraCoach } from "@/components/app/AuraCoach";
import { ProfessionSelect } from "@/components/app/ProfessionSelect";
import { StoryViewer, StoryComposer } from "@/components/app/Stories";
import { OwnAuramaxChip } from "@/components/app/WalletConnect";
import { ProfileCompletion } from "@/components/app/ProfileCompletion";
import { ReportDialog, BlockConfirm } from "@/components/app/ReportDialog";
import { Modal } from "@/components/ui/modal";
import { PostCard } from "@/components/feed/PostCard";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl, fmt, shareUrl, shareLink } from "@/lib/api";
import { cn } from "@/lib/utils";

const BASE_TABS = ["Posts", "Reposts", "Media"];

export default function Profile({ usernameOverride }) {
  const params = useParams();
  const username = usernameOverride || params.username || "me";
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [tab, setTab] = useState("Posts");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [coinOpen, setCoinOpen] = useState(false);
  const [listOpen, setListOpen] = useState(null);
  const [menu, setMenu] = useState(false);
  const [composer, setComposer] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [avatarMenu, setAvatarMenu] = useState(false);
  const [pfpBusy, setPfpBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [notFound, setNotFound] = useState("");
  const pfpRef = useRef(null);

  const uname = username === "me" ? (user && user.username) : username;

  const load = useCallback(async () => {
    if (!uname) return;
    setLoading(true);
    try {
      const p = await api.getUser(uname);
      setProfile(p);
      if (tab === "Saved") {
        setPosts(p.is_self ? await api.mySaved() : []);
      } else {
        const type = tab === "Reposts" ? "reposts" : tab === "Media" ? "media" : undefined;
        setPosts(await api.userPosts(uname, type));
      }
    } catch (e) { setProfile(null); setNotFound(e.message); }
    finally { setLoading(false); }
    // api + setState setters are stable; only uname/tab drive a reload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uname, tab]);

  useEffect(() => { load(); }, [load]);

  const isMe = profile && user && profile.user_id === user.user_id;
  const share = async () => {
    const r = await shareLink(shareUrl(profile.username), `${profile.name} (@${profile.username}) on AuraMax`);
    if (r === "copied") toast("Profile link copied"); else if (r === "failed") toast(shareUrl(profile.username));
  };

  const viewStory = async () => {
    try { const g = await api.userStories(uname); if (g.stories?.length) setViewer({ groups: [g], index: 0 }); }
    catch (e) { console.error("viewStory failed", e); }
  };
  const onAvatarTap = () => {
    if (isMe) { setAvatarMenu(true); return; }
    viewStory();
  };
  const changePhoto = async (file) => {
    if (!file) return;
    setPfpBusy(true);
    try {
      const r = await api.uploadMedia(file);
      const p = await api.updateProfile({ picture: r.url });
      setProfile((cur) => ({ ...cur, ...p, picture: r.url }));
      await checkAuth();
      toast("Profile photo updated");
      setAvatarMenu(false);
    } catch (e) { toast(e.message); }
    finally { setPfpBusy(false); }
  };
  const removePhoto = async () => {
    setPfpBusy(true);
    try {
      const p = await api.updateProfile({ picture: "" });
      setProfile((cur) => ({ ...cur, ...p, picture: "" }));
      await checkAuth();
      toast("Profile photo removed");
      setAvatarMenu(false);
    } catch (e) { toast(e.message); }
    finally { setPfpBusy(false); }
  };

  const message = async () => { try { const c = await api.startConversation(uname); navigate(`/messages?c=${c.id}`); } catch (e) { toast(e.message); } };
  const moderate = async (fn, msg) => { try { await fn(); toast(msg); setMenu(false); load(); } catch (e) { toast(e.message); } };

  if (loading && !profile) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-am-text3" /></div>;
  if (!profile) return <div className="py-24 text-center text-am-text2" data-testid="profile-unavailable">{notFound || "This account doesn't exist."}</div>;

  return (
    <div className="flex">
      <div className="min-w-0 flex-1">
        <div className="relative h-40 w-full overflow-hidden bg-am-surface2 sm:h-52 md:h-60">
          {profile.banner ? <img src={mediaUrl(profile.banner)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full aura-gradient opacity-25" />}
          <div className="absolute inset-0 bg-gradient-to-t from-am-bg via-am-bg/10 to-transparent" />
          <button onClick={() => navigate(-1)} className="absolute left-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white backdrop-blur-md md:hidden"><ArrowLeft className="h-5 w-5" /></button>
        </div>

        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <div className="-mt-12 flex items-end justify-between md:-mt-14">
            <button onClick={onAvatarTap} data-testid="profile-avatar">
              <AuraRing src={mediaUrl(profile.picture) || undefined} size={104} ring={3} fallback={profile.name} story={profile.has_story} storySeen={false} />
            </button>
            <input ref={pfpRef} type="file" hidden accept="image/*" onChange={(e) => changePhoto(e.target.files[0])} />
            <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
              <button onClick={share} data-testid="profile-share" title="Share profile" className="grid h-10 w-10 place-items-center rounded-full border border-am-border-strong text-white hover:bg-white/5"><Share2 className="h-4 w-4" /></button>
              {isMe ? (
                <>
                  <AuraCoach />
                  <button onClick={() => setComposer(true)} data-testid="profile-add-story" className="rounded-full border border-am-border-strong px-4 py-2 text-sm font-semibold text-white hover:bg-white/5">+ Story</button>
                  <button onClick={() => setEdit(true)} data-testid="edit-profile" className="rounded-full border border-am-border-strong px-4 py-2 text-sm font-semibold text-white hover:bg-white/5">Edit Profile</button>
                </>
              ) : profile.is_blocked ? (
                <button onClick={() => moderate(() => api.unblock(uname), "Unblocked")} data-testid="profile-unblock" className="rounded-full border border-aura-magenta/50 px-4 py-2 text-sm font-semibold text-aura-magenta hover:bg-white/5">Unblock</button>
              ) : (
                <>
                  <button onClick={message} data-testid="profile-message" className="grid h-10 w-10 place-items-center rounded-full border border-am-border-strong text-white hover:bg-white/5"><Mail className="h-4 w-4" /></button>
                  <FollowButton username={uname} state={profile.is_following ? (profile.follows_me ? "friends" : "following") : (profile.follows_me ? "follow_back" : "follow")} followsMe={profile.follows_me} size="md" testid="profile-follow" onChange={() => load()} />
                  <div className="relative">
                    <button onClick={() => setMenu((m) => !m)} data-testid="profile-more" className="grid h-10 w-10 place-items-center rounded-full border border-am-border-strong text-white hover:bg-white/5"><MoreHorizontal className="h-4 w-4" /></button>
                    {menu && (
                      <div className="absolute right-0 top-11 z-30 w-48 overflow-hidden rounded-xl border border-am-border bg-am-surface2 py-1 shadow-xl">
                        <button onClick={() => moderate(() => (profile.is_muted ? api.unmute(uname) : api.mute(uname)), profile.is_muted ? "Unmuted" : "Muted")} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-am-text hover:bg-white/5"><VolumeX className="h-4 w-4" /> {profile.is_muted ? "Unmute" : "Mute"}</button>
                        <button onClick={() => { setMenu(false); setBlockOpen(true); }} data-testid="profile-block" className="flex w-full items-center gap-2 px-3 py-2 text-sm text-am-text hover:bg-white/5"><Ban className="h-4 w-4" /> Block @{profile.username}</button>
                        <button onClick={() => { setMenu(false); setReportOpen(true); }} data-testid="profile-report" className="flex w-full items-center gap-2 px-3 py-2 text-sm text-am-text hover:bg-white/5"><Flag className="h-4 w-4" /> Report</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {profile.is_blocked && <div data-testid="profile-blocked-banner" className="mt-3 rounded-xl border border-aura-magenta/30 bg-aura-magenta/10 px-3 py-2 text-xs text-aura-magenta">You blocked @{profile.username}. Their posts are hidden and neither of you can follow or message the other.</div>}

          <div className="mt-3">
            <div className="flex items-center gap-1.5">
              <h1 className="font-display text-xl font-extrabold text-white">{profile.name}</h1>
              {profile.is_verified && <VerifiedBadge size={18} />}{profile.is_og && <OgBadge size={18} />}{profile.genesis_ambassador && <span data-testid="genesis-ambassador-badge" title="Genesis Ambassador" className="rounded-full border border-aura-gold/50 px-2 py-0.5 text-[10px] font-bold uppercase text-aura-gold">Ambassador</span>}
              {profile.is_ai_creator && <span className="rounded-full border border-am-border bg-am-surface2 px-2 py-0.5 text-[10px] font-semibold text-am-text2">AI Creator</span>}
            </div>
            <div className="text-sm text-am-text2">@{profile.username}</div>
            {profile.show_category !== false && profile.category && <div className="mt-2 inline-block rounded-full border border-am-border bg-am-surface2 px-2.5 py-0.5 text-xs text-am-text">{profile.category}</div>}
            {profile.bio && <p className="mt-2 whitespace-pre-wrap text-sm text-am-text">{profile.bio}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-am-text2">
              {profile.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {profile.location}</span>}
            </div>
            {profile.links?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {profile.links.map((l, i) => <a key={`${l.url}-${i}`} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-full border border-am-border bg-am-surface2 px-3 py-1 text-xs font-medium text-aura-cyan hover:bg-white/5"><LinkIcon className="h-3 w-3" /> {l.label}</a>)}
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <button onClick={() => setListOpen("followers")} data-testid="profile-followers"><span className="font-bold text-white">{fmt(profile.followers)}</span> <span className="text-am-text2">Followers</span></button>
              <button onClick={() => setListOpen("following")} data-testid="profile-following"><span className="font-bold text-white">{fmt(profile.following)}</span> <span className="text-am-text2">Following</span></button>
              <span><span className="font-bold text-white">{fmt(profile.posts)}</span> <span className="text-am-text2">Posts</span></span>
              <span className="mx-1 h-4 w-px bg-am-border" />
              <AuraBadge profile={profile} self={isMe} size="sm" />
              {isMe && <OwnAuramaxChip onClick={() => navigate("/balance")} />}
            </div>
            {isMe && (profile.aura_streak_days > 0 ? (
              <div className={cn("mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold text-white", [7,14,30,50,100,365].includes(profile.aura_streak_days) ? "border-transparent bg-clip-padding aura-gradient !text-black" : "border-aura-gold/30 bg-am-surface2")} data-testid="profile-streak"><Flame className={cn("h-3.5 w-3.5", [7,14,30,50,100,365].includes(profile.aura_streak_days) ? "text-black" : "text-aura-gold")} /> {profile.aura_streak_days} day streak · <span className={[7,14,30,50,100,365].includes(profile.aura_streak_days) ? "text-black" : "aura-text"}>{((profile.aura_multiplier_bps||10000)/10000).toFixed(2)}x Aura</span></div>
            ) : (
              <button onClick={() => navigate("/home")} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-am-text2 hover:text-white" data-testid="profile-start-streak"><Flame className="h-3.5 w-3.5 text-aura-gold" /> Start your streak — post today →</button>
            ))}
          </div>

          {isMe && <ProfileCompletion refreshKey={`${profile.picture}|${profile.banner}|${profile.bio}|${profile.posts}|${profile.following}`} onEdit={() => setEdit(true)} />}
          {isMe && (
            <button onClick={() => navigate("/referrals")} data-testid="profile-invite" className="mt-3 flex w-full items-center gap-3 rounded-2xl border border-am-border bg-am-surface2 p-3 text-left hover:bg-white/5">
              <span className="grid h-10 w-10 place-items-center rounded-xl aura-gradient"><Users className="h-5 w-5 text-black" /></span>
              <div className="flex-1"><div className="text-sm font-bold text-white">Invite friends</div><div className="text-xs text-am-text2">Your username is your referral code.</div></div>
            </button>
          )}

          <button onClick={() => setCoinOpen(true)} data-testid="profile-coin" className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-am-border bg-am-surface2 p-3 text-left hover:bg-white/5">
            <span className="grid h-10 w-10 place-items-center rounded-xl aura-gradient"><Coins className="h-5 w-5 text-black" /></span>
            <div className="flex-1"><div className="text-sm font-bold text-white">Personal Token</div><div className="text-xs text-am-text2">Coming soon — one on-chain token per identity</div></div>
          </button>

          <div className="mt-5 flex border-b border-am-border">
            {(isMe ? [...BASE_TABS, "Saved"] : BASE_TABS).map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`profile-tab-${t.toLowerCase()}`} className={cn("relative px-4 py-3 text-sm font-semibold", tab === t ? "text-white" : "text-am-text2")}>
                {t}{tab === t && <span className="absolute bottom-0 left-1/2 h-[3px] w-8 -translate-x-1/2 rounded-full aura-gradient" />}
              </button>
            ))}
          </div>

          <div className="py-4">
            {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-am-text3" /></div>
              : posts.length === 0 ? <p className="py-12 text-center text-sm text-am-text3" data-testid="profile-empty">{tab === "Reposts" ? "No reposts yet." : tab === "Saved" ? "Posts you save are private and appear here." : tab === "Media" ? "No media yet." : isMe ? "You haven't posted yet." : "No posts yet."}</p>
              : <div className="space-y-4">{posts.map((p) => <PostCard key={p.id + (p.reposted_by?.user_id || "")} post={p} onDeleted={(id) => { setPosts((x) => x.filter((y) => y.id !== id)); setProfile((pr) => ({ ...pr, posts: pr.posts - 1 })); }} />)}</div>}
          </div>
        </div>
      </div>
      <RightSidebar />

      {edit && <EditProfile profile={profile} onClose={() => setEdit(false)} onSaved={(p) => { setProfile((cur) => ({ ...cur, ...p })); setEdit(false); }} />}      {listOpen && <PeopleList username={uname} kind={listOpen} onClose={() => setListOpen(null)} navigate={navigate} />}
      <StoryComposer open={composer} onClose={() => setComposer(false)} onPosted={load} />
      {profile && !isMe && <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} targetType="user" targetId={profile.user_id} label={`@${profile.username}`} />}
      {profile && !isMe && <BlockConfirm open={blockOpen} onClose={() => setBlockOpen(false)} username={profile.username} onBlocked={load} />}
      {viewer && <StoryViewer groups={viewer.groups} startIndex={viewer.index} mine={isMe ? viewer.groups[0] : null} onClose={() => setViewer(null)} onViewed={load} />}

      <Modal open={avatarMenu} onClose={() => !pfpBusy && setAvatarMenu(false)} title="Profile photo" testid="avatar-menu">
        <div className="p-3">
          {profile?.has_story && (
            <button onClick={() => { setAvatarMenu(false); viewStory(); }} data-testid="avatar-view-story" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-white hover:bg-white/5"><span className="grid h-9 w-9 place-items-center rounded-full bg-am-surface2"><Eye className="h-4 w-4 aura-text" /></span> View your story</button>
          )}
          <button onClick={() => { setAvatarMenu(false); setComposer(true); }} data-testid="avatar-add-story" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-white hover:bg-white/5"><span className="grid h-9 w-9 place-items-center rounded-full bg-am-surface2"><Plus className="h-4 w-4 aura-text" /></span> Add to your story</button>
          <button onClick={() => pfpRef.current?.click()} disabled={pfpBusy} data-testid="avatar-change-photo" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-white hover:bg-white/5 disabled:opacity-60"><span className="grid h-9 w-9 place-items-center rounded-full bg-am-surface2">{pfpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4 aura-text" />}</span> {profile?.picture ? "Change profile photo" : "Upload profile photo"}</button>
          {profile?.picture && (
            <button onClick={removePhoto} disabled={pfpBusy} data-testid="avatar-remove-photo" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-aura-magenta hover:bg-white/5 disabled:opacity-60"><span className="grid h-9 w-9 place-items-center rounded-full bg-am-surface2"><X className="h-4 w-4" /></span> Remove current photo</button>
          )}
        </div>
      </Modal>

      <Modal open={coinOpen} onClose={() => setCoinOpen(false)} title="Personal Token" testid="coin-info-modal">
        <div className="p-5 text-center">
          <span className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl aura-gradient"><Coins className="h-6 w-6 text-black" /></span>
          <h3 className="font-display text-lg font-bold text-white">Create Your Personal Token — Coming Soon</h3>
          <p className="mx-auto mt-2 max-w-sm text-sm text-am-text2">Build an on-chain asset around your identity, audience, and personal brand. Eligible after identity verification. Token creation isn't enabled yet.</p>
        </div>
      </Modal>
    </div>
  );
}

function PeopleList({ username, kind, onClose, navigate }) {
  const [people, setPeople] = useState(null);
  useEffect(() => { (kind === "followers" ? api.followers(username) : api.following(username)).then(setPeople).catch(() => setPeople([])); }, [username, kind]);
  return (
    <Modal open onClose={onClose} title={kind === "followers" ? "Followers" : "Following"} testid="people-list">
      <div className="max-h-[70vh] space-y-1 overflow-y-auto p-4">
        {people === null ? <p className="text-center text-sm text-am-text3">Loading…</p>
          : people.length === 0 ? <p className="py-8 text-center text-sm text-am-text3">{kind === "followers" ? "No followers yet." : "Not following anyone yet."}</p>
          : people.map((p) => (
            <div key={p.user_id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-white/5">
              <button onClick={() => { onClose(); navigate(`/@${p.username}`); }} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                <AuraRing src={mediaUrl(p.picture) || undefined} size={40} ring={2} fallback={p.name} />
                <div className="min-w-0"><div className="flex items-center gap-1 text-sm font-semibold text-white"><span className="truncate">{p.name}</span>{p.is_verified && <VerifiedBadge size={13} />}</div><div className="truncate text-xs text-am-text2">@{p.username}</div></div>
              </button>
              {!p.is_self && <FollowButton username={p.username} state={p.state || "follow"} followsMe={p.follows_me} />}
            </div>
          ))}
      </div>
    </Modal>
  );
}

function EditProfile({ profile, onClose, onSaved }) {
  const [form, setForm] = useState({ name: profile.name || "", bio: profile.bio || "", category: profile.category || "", location: profile.location || "", picture: profile.picture || "", banner: profile.banner || "", links: profile.links || [], show_category: profile.show_category !== false });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [uname, setUname] = useState(profile.username || "");
  const [avail, setAvail] = useState(null);
  const [checking, setChecking] = useState(false);
  const [uErr, setUErr] = useState("");
  const { checkAuth } = useAuth();
  const picRef = useRef(null); const banRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const norm = (s) => (s || "").trim().replace(/^@/, "").toLowerCase();
  const changed = norm(uname) !== norm(profile.username);

  useEffect(() => {
    setUErr("");
    if (!changed) { setAvail(null); return; }
    const v = norm(uname);
    if (!/^[a-z0-9_]{2,20}$/.test(v)) { setAvail(false); setUErr("2–20 letters, numbers or underscores."); return; }
    let active = true; setChecking(true);
    const t = setTimeout(async () => {
      try { const r = await api.usernameAvailable(v); if (active) { setAvail(r.available); if (!r.available) setUErr(r.reason || "That username is taken."); } }
      catch { if (active) setAvail(null); } finally { if (active) setChecking(false); }
    }, 350);
    return () => { active = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uname]);

  const upload = async (file, key) => { if (!file) return; setBusy(key); try { const r = await api.uploadMedia(file); set(key, r.url); } catch (e) { toast(e.message); } finally { setBusy(""); } };
  const addLink = () => set("links", [...form.links, { label: "", url: "" }]);
  const setLink = (i, k, v) => set("links", form.links.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const rmLink = (i) => set("links", form.links.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      if (changed) {
        if (avail !== true) { toast(uErr || "Choose an available username."); setSaving(false); return; }
        await api.setUsername(norm(uname));
      }
      const p = await api.updateProfile(form);
      await checkAuth();
      onSaved({ ...p, username: changed ? norm(uname) : p.username });
      toast("Profile saved");
    }
    catch (e) { toast(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="Edit Profile" testid="edit-profile-modal">
      <div className="max-h-[80vh] overflow-y-auto p-4">
        <div className="relative mb-10 h-28 overflow-hidden rounded-xl bg-am-surface2">
          {form.banner ? <img src={mediaUrl(form.banner)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full aura-gradient opacity-25" />}
          <div className="absolute right-2 top-2 flex gap-1.5">
            <button onClick={() => banRef.current?.click()} data-testid="edit-banner" className="grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white">{busy === "banner" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}</button>
            {form.banner && <button onClick={() => set("banner", "")} data-testid="edit-banner-remove" className="grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white"><X className="h-4 w-4" /></button>}
          </div>
          <input ref={banRef} type="file" hidden accept="image/*" onChange={(e) => upload(e.target.files[0], "banner")} />
          <div className="absolute -bottom-8 left-4">
            <div className="relative"><AuraRing src={mediaUrl(form.picture) || undefined} size={64} ring={2.5} fallback={form.name} />
              <button onClick={() => picRef.current?.click()} data-testid="edit-avatar" className="absolute -bottom-1 -right-1 grid h-7 w-7 place-items-center rounded-full border-2 border-am-bg aura-gradient text-black">{busy === "picture" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}</button>
            </div>
            <input ref={picRef} type="file" hidden accept="image/*" onChange={(e) => upload(e.target.files[0], "picture")} />
          </div>
          {form.picture && <button onClick={() => set("picture", "")} data-testid="edit-avatar-remove" className="absolute -bottom-6 left-20 text-[11px] font-semibold text-am-text2 hover:text-white">Remove photo</button>}
        </div>

        <label className="mb-1 block text-xs font-semibold text-am-text2">Display name</label>
        <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-name" className="mb-3 w-full rounded-xl border border-am-border bg-am-surface2 px-3 py-2.5 text-sm text-white outline-none focus:border-am-border-strong" />
        <label className="mb-1 block text-xs font-semibold text-am-text2">Username</label>
        <div className="mb-1 flex items-center rounded-xl border border-am-border bg-am-surface2 px-3 focus-within:border-am-border-strong">
          <span className="text-sm text-am-text3">@</span>
          <input value={uname} onChange={(e) => setUname(e.target.value)} data-testid="edit-username" className="w-full bg-transparent px-1 py-2.5 text-sm text-white outline-none" />
          {checking ? <Loader2 className="h-4 w-4 animate-spin text-am-text3" /> : changed && avail === true ? <Check className="h-4 w-4 text-aura-mint" /> : changed && avail === false ? <X className="h-4 w-4 text-aura-magenta" /> : null}
        </div>
        {uErr ? <p data-testid="edit-username-error" className="mb-3 text-xs text-aura-magenta">{uErr}</p>
          : changed && avail === true ? <p className="mb-3 text-xs text-aura-mint">@{norm(uname)} is available</p>
          : <p className="mb-3 text-[11px] text-am-text3">You can change your username once a week.</p>}
        <label className="mb-1 block text-xs font-semibold text-am-text2">Bio</label>
        <textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="edit-bio" rows={2} className="mb-3 w-full resize-none rounded-xl border border-am-border bg-am-surface2 px-3 py-2.5 text-sm text-white outline-none focus:border-am-border-strong" />
        <label className="mb-1 block text-xs font-semibold text-am-text2">Profession</label>
        <div className="mb-2"><ProfessionSelect value={form.category} onChange={(v) => set("category", v)} testid="edit-profession" /></div>
        <label className="mb-3 flex items-center justify-between rounded-xl border border-am-border bg-am-surface2 px-3 py-2.5">
          <span className="text-sm text-am-text">Display profession on profile</span>
          <button onClick={() => set("show_category", !form.show_category)} data-testid="edit-show-profession" className={cn("relative h-6 w-11 rounded-full transition-colors", form.show_category ? "aura-gradient" : "bg-am-surface3")}>
            <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all", form.show_category ? "left-[22px]" : "left-0.5")} />
          </button>
        </label>
        <label className="mb-1 block text-xs font-semibold text-am-text2">Location</label>
        <input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="edit-location" placeholder="Optional" className="mb-3 w-full rounded-xl border border-am-border bg-am-surface2 px-3 py-2.5 text-sm text-white outline-none focus:border-am-border-strong" />

        <div className="mb-3">
          <label className="mb-1 block text-xs font-semibold text-am-text2">Links</label>
          {form.links.map((l, i) => (
            <div key={l.key || i} className="mb-2 flex gap-2">
              <input value={l.label} onChange={(e) => setLink(i, "label", e.target.value)} placeholder="Label" className="w-28 rounded-lg border border-am-border bg-am-surface2 px-2.5 py-2 text-sm text-white outline-none" />
              <input value={l.url} onChange={(e) => setLink(i, "url", e.target.value)} placeholder="https://…" className="flex-1 rounded-lg border border-am-border bg-am-surface2 px-2.5 py-2 text-sm text-white outline-none" />
              <button onClick={() => rmLink(i)} className="grid w-9 place-items-center rounded-lg border border-am-border text-am-text2"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={addLink} data-testid="edit-add-link" className="flex items-center gap-1.5 text-sm font-semibold aura-text"><Plus className="h-4 w-4" /> Add Social</button>
        </div>

        <button onClick={save} disabled={saving} data-testid="edit-save" className="w-full rounded-full aura-gradient py-3 text-sm font-bold text-black disabled:opacity-60">{saving ? "Saving…" : "Save profile"}</button>
      </div>
    </Modal>
  );
}
